import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, message } from "antd";
import FormModal from "../../components/FormModal";
import PasswordFields from "../../components/PasswordFields";
import { extractMsg } from "../../utils/errorUtils";
import {
  fetchPermissions,
  grantPermission,
  revokePermission,
} from "./DGHelperUsersAPI";

export function AddUserDialog({ open, onClose, onSave, rolesList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to create user"));
      console.error("Failed to create user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New DGHelper User"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Create User"
    >
      <Form.Item
        name="username"
        label="Username"
        rules={[
          { required: true, message: "Username is required" },
          { min: 6, message: "Username must be at least 6 characters long" },
          { max: 20, message: "Username must not exceed 20 characters" },
        ]}
      >
        <Input placeholder="User Name" />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" placeholder="user@example.com" />
      </Form.Item>
      <PasswordFields />
      <Form.Item
        name="role"
        label="Role"
        rules={[{ required: true, message: "Role is required" }]}
      >
        <Select placeholder="Select a role" allowClear>
          {rolesList.map((r) => (
            <Select.Option key={r.id || r.name} value={r.name || r.id}>
              {r.displayName || r.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </FormModal>
  );
}

export function EditUserDialog({ open, user, onClose, onSave, rolesList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        username: user.username || user.name,
        email: user.email,
        password: "",
        confirmPassword: "",
      });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to edit user"));
      console.error("Failed to edit user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title={`Edit User: ${user?.username || user?.name || ""}`}
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Save Changes"
    >
      <Form.Item name="username" label="Username">
        <Input disabled />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" placeholder="user@example.com" />
      </Form.Item>
      <PasswordFields required={false} />
    </FormModal>
  );
}

export function ManagePermissionsDialog({
  open,
  user,
  onClose,
  onSuccess,
  messageApi,
}) {
  const [permsList, setPermsList] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPermissions()
      .then(setPermsList)
      .catch((err) => {
        console.error("Failed to fetch permissions:", err);
        message.error(extractMsg(err, "Failed to fetch permissions"));
      });
  }, []);

  useEffect(() => {
    if (open && user) {
      setSelectedPermissions((user.permissions || []).map((p) => p.name));
    }
  }, [open, user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const initialPerms = user?.permissions || [];
      const selectedPerms = selectedPermissions || [];
      const toGrant = selectedPerms.filter((p) => !initialPerms.includes(p));
      const toRevoke = initialPerms.filter((p) => !selectedPerms.includes(p));

      for (const pName of toGrant) {
        await grantPermission(user.id, pName);
      }
      for (const pName of toRevoke) {
        await revokePermission(user.id, pName.name || pName);
      }
      messageApi.success("Permissions updated successfully.");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to update permissions", error);
      messageApi.error(extractMsg(error, "Failed to update permissions."));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Manage Permissions: ${user?.username || user?.name || "User"}`}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
    >
      <Select
        mode="multiple"
        value={selectedPermissions}
        onChange={setSelectedPermissions}
        placeholder="Select permissions"
        style={{ width: "100%" }}
      >
        {permsList.map((p) => (
          <Select.Option key={p.id} value={p.name}>
            {p.name}
          </Select.Option>
        ))}
      </Select>
    </Modal>
  );
}
